import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Calendar, GraduationCap, Flame,
  LogIn, Mail, Lock, Sparkles, Target, UserPlus, X, Loader2, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../lms/contexts/AuthContext";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import BatchCover, { batchHasCoverVideo } from "./BatchCover";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StudentAccessModal — single-entry popup that replaces the old "Enroll Now"
 * button. Shows:
 *  1. A stylised student-portal login form at the top.
 *  2. Beneath it, all currently-active MDCAT batches the student can apply to
 *     (for users who don't yet have an account).
 *
 * Reuses the same visual grammar as the /apply page so admins' batch data
 * flows through one canonical card everywhere.
 */
export default function StudentAccessModal({ open, onClose }) {
  const navigate = useNavigate();
  const { login: authLogin, refreshMe } = useAuth();
  const [batches, setBatches] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [siteWhatsApp, setSiteWhatsApp] = useState("");
  // OTP-challenge state — shown when the backend asks for new-device verification.
  // Mirrors the flow in /login so the inline modal works on mobile devices that
  // students haven't trusted yet.
  const [otp, setOtp] = useState(null); // { challenge_id, email_hint, device_label, city }
  const [otpCode, setOtpCode] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setOtp(null);
    setOtpCode("");
    axios
      .get(`${API}/batches/open-for-registration`)
      .then((r) => setBatches(Array.isArray(r.data) ? r.data.filter((b) => b.display_on_landing !== false) : []))
      .catch(() => setBatches([]));
    axios
      .get(`${API}/site/config`)
      .then((r) => setSiteWhatsApp(r.data?.whatsapp_number || ""))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErr("Please enter your email and password.");
      return;
    }
    setLoading(true); setErr("");
    try {
      // Go through AuthProvider so the user state updates immediately —
      // otherwise RoleRoute still sees user=false and bounces back to /login.
      const u = await authLogin(email.trim(), password);
      // New-device verification path — show inline OTP step instead of
      // closing the modal. Without this branch, students on a fresh phone
      // see a blank navy portal because `u` is the OTP challenge payload
      // (no role) and RoleRoute redirects in a loop.
      if (u?.otp_required) {
        setOtp({
          challenge_id: u.challenge_id,
          email_hint: u.email_hint,
          device_label: u.device_label,
          city: u.city,
        });
        return;
      }
      onClose();
      // Force-change password flow for new admin/mentor seeds
      if (u?.must_change_password) {
        navigate("/change-password");
        return;
      }
      // Route based on role so admins/mentors don't land on /dashboard (student-only).
      if (u?.role === "admin") navigate("/admin");
      else if (u?.role === "mentor") navigate("/mentor");
      else navigate("/student");
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      if (ex?.response?.status === 503 && typeof detail === "string" && detail.toLowerCase().includes("verification email")) {
        setErr("We couldn't send the sign-in code just now — wait a few seconds and try again.");
      } else {
        setErr(detail || "Login failed. Please try again.");
      }
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!otp?.challenge_id) return;
    if (!otpCode.trim()) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, {
        challenge_id: otp.challenge_id,
        code: otpCode.trim(),
      });
      // Sync AuthContext.user from /auth/me so the route guards see a real
      // user object (with role) before we navigate.
      await refreshMe();
      onClose();
      if (data?.must_change_password) {
        navigate("/change-password");
        return;
      }
      if (data?.role === "admin") navigate("/admin");
      else if (data?.role === "mentor") navigate("/mentor");
      else navigate("/student");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Verification failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-ink-900/85 backdrop-blur-md flex items-start md:items-center justify-center px-3 py-4 md:p-6 overflow-y-auto"
        onClick={onClose}
        data-testid="student-access-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 220 }}
          className="relative w-full max-w-2xl rounded-3xl border border-white/10 bg-gradient-to-br from-ink-800/95 to-ink-900/97 backdrop-blur-2xl overflow-hidden my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div aria-hidden className="absolute top-0 right-0 w-72 h-72 cinematic-dots-dense opacity-15 pointer-events-none" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center border border-white/15 hover:border-cyan-glow/40 hover:text-cyan-glow text-white/80 transition bg-black/30 backdrop-blur"
            aria-label="Close"
            data-testid="student-access-close"
          >
            <X size={16} />
          </button>

          {/* ---- LOGIN — stylised as a button-like card ---- */}
          <div className="relative p-6 md:p-8">
            {otp ? (
              <>
                <div className="mono-label text-cyan-glow mb-1.5 text-[10px] md:text-xs">— New-device verification</div>
                <h3 className="display-serif text-white text-2xl md:text-3xl leading-tight">
                  Check your email for the <span className="display-italic text-cyan-glow">code</span>.
                </h3>
                <p className="font-body text-white/60 text-xs md:text-sm mt-3">
                  We sent a 6-digit sign-in code to <span className="text-white">{otp.email_hint}</span>.
                  {otp.device_label ? <> Enter it below to finish signing in on <span className="text-white">{otp.device_label}</span>{otp.city ? <> · {otp.city}</> : null}.</> : null}
                </p>
                <form onSubmit={submitOtp} className="mt-5 space-y-3" data-testid="student-access-otp-form">
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="••••••"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="w-full h-14 px-5 rounded-full bg-white/[0.04] border border-white/10 text-white text-center text-2xl tracking-[0.4em] focus:border-cyan-glow/60 focus:bg-white/[0.06] outline-none transition placeholder:text-white/25"
                      data-testid="student-access-otp-code"
                      autoFocus
                    />
                  </div>
                  {err && (
                    <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2" data-testid="student-access-otp-error">
                      {err}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading || otpCode.length < 6}
                    className="btn-primary w-full justify-center text-sm md:text-base h-12 disabled:opacity-60"
                    data-testid="student-access-otp-submit"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                    {loading ? "Verifying…" : "Verify & sign in"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOtp(null); setOtpCode(""); setErr(""); }}
                    className="w-full text-[11px] md:text-xs text-white/55 hover:text-cyan-glow transition"
                    data-testid="student-access-otp-back"
                  >
                    ← Use a different email
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="mono-label text-cyan-glow mb-1.5 text-[10px] md:text-xs">— Welcome back, Logician</div>
                <h3 className="display-serif text-white text-2xl md:text-3xl leading-tight">
                  Sign in to your <span className="display-italic text-cyan-glow">portal</span>.
                </h3>

                <form onSubmit={submit} className="mt-5 space-y-3" data-testid="student-access-login-form">
                  <div className="relative">
                    <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                      className="w-full h-12 pl-11 pr-4 rounded-full bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35 text-sm focus:border-cyan-glow/60 focus:bg-white/[0.06] outline-none transition"
                      data-testid="student-access-email"
                    />
                  </div>
                  <div className="relative">
                    <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="password"
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full h-12 pl-11 pr-4 rounded-full bg-white/[0.04] border border-white/10 text-white placeholder:text-white/35 text-sm focus:border-cyan-glow/60 focus:bg-white/[0.06] outline-none transition"
                      data-testid="student-access-password"
                    />
                  </div>
                  {err && (
                    <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2" data-testid="student-access-error">
                      {err}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center text-sm md:text-base h-12 disabled:opacity-60"
                    data-testid="student-access-submit"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                    {loading ? "Signing in…" : "Sign in to student portal"}
                    {!loading && <ArrowRight size={15} />}
                  </button>
                  <div className="flex items-center justify-between text-[11px] md:text-xs pt-0.5">
                    <Link
                      to="/forgot-password"
                      onClick={onClose}
                      className="text-white/55 hover:text-cyan-glow transition"
                      data-testid="student-access-forgot"
                    >
                      Forgot password?
                    </Link>
                    <Link
                      to="/login"
                      onClick={onClose}
                      className="text-cyan-glow hover:text-white transition inline-flex items-center gap-1"
                      data-testid="student-access-fullpage"
                    >
                      Full sign-in page <ArrowUpRight size={11} />
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* ---- New here? Pick a batch to apply ---- */}
          <div className="relative border-t border-white/5 bg-ink-900/50 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-1.5">
              <UserPlus size={14} className="text-[#ffcb47]" />
              <span className="mono-label text-[#ffcb47] text-[10px] md:text-xs">— New to LogicsWallah?</span>
            </div>
            <h4 className="display-serif text-white text-lg md:text-2xl leading-tight">
              Pick a batch to <span className="display-italic text-[#ffcb47]">apply</span>.
            </h4>
            <p className="font-body text-white/55 text-xs md:text-sm mt-2">
              Every batch has its own application form — we review each one personally.
            </p>

            <div className="mt-4 md:mt-5 space-y-2.5" data-testid="student-access-batches">
              {batches === null ? (
                <>
                  <div className="lms-shimmer h-16" />
                  <div className="lms-shimmer h-16" />
                </>
              ) : batches.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-center">
                  <Sparkles size={18} className="mx-auto text-[#ffcb47] mb-2" />
                  <div className="font-body text-white text-sm">No batches accepting applications right now.</div>
                  <div className="mono-label text-white/45 mt-1 text-[10px]">Check back soon.</div>
                </div>
              ) : (
                batches.map((b, i) => <AccessBatchRow key={b.id} batch={b} index={i} siteWhatsApp={siteWhatsApp} />)
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AccessBatchRow({ batch, index, siteWhatsApp }) {
  const status = batch.status || (batch.registration_open ? "open" : "closed");
  // `upcoming` batches are also accepting applications — students can pre-enrol before classes start.
  const canApply = status === "open" || status === "upcoming";
  const Icon =
    batch.type === "parallel" ? GraduationCap : batch.type === "repeaters" ? Target : Flame;
  const waNumber = (batch.whatsapp_number || siteWhatsApp || "").toString().replace(/\D/g, "");
  const waMsg =
    `Hi LogicsWallah! I'm interested in *${batch.name}*` +
    (batch.price_one_time ? ` (Rs. ${Number(batch.price_one_time).toLocaleString()})` : "") +
    `. Can you share more details?`;
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMsg)}` : null;

  // Video / banner branching:
  //   • Has a cover_video → mount the iframe directly with poster autoplay
  //     (no "Tap to play" clutter, no "Batch Preview" fallback text).
  //   • Has only a cover_image → render at the admin's chosen 1600×540
  //     banner aspect so promo art (e.g. "70% OFF") doesn't get squished
  //     into a 16:9 letterbox.
  //   • Neither → soft gradient placeholder, no text.
  const hasVideo = batchHasCoverVideo(batch);
  const hasBanner = !hasVideo && !!batch.cover_image;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index }}
      className="relative rounded-2xl border border-white/10 bg-ink-800/40 hover:bg-ink-800/70 hover:border-cyan-glow/40 transition-all overflow-hidden"
      data-testid={`access-batch-${batch.slug}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-stretch">
        {/* Thumbnail —
              • cover_video  → 16:9 player frame (BatchCover mounts iframe)
              • cover_image  → admin's native 1600×540 banner aspect, NOT
                cropped to 16:9, so promo art stays pixel-true on every
                viewport.
              • neither      → quiet gradient placeholder.
            Wider on tablet+ (sm:w-44 md:w-56) so the cover is actually
            legible. */}
        <div
          className={`relative w-full sm:w-44 md:w-56 ${hasVideo ? "aspect-video" : hasBanner ? "aspect-[1600/540] sm:aspect-auto sm:min-h-[124px]" : "aspect-[1600/540] sm:aspect-auto"} shrink-0 overflow-hidden bg-[#0b1530]`}
          data-testid={`access-batch-cover-${batch.slug}`}
        >
          {hasVideo ? (
            <BatchCover
              batch={batch}
              alt={batch.name}
              interactive={true}
              showWatchChip={false}
              autoplayDelayMs={300}
            />
          ) : hasBanner ? (
            <img
              src={batch.cover_image}
              alt={batch.name}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.3), rgba(255,203,71,0.2), rgba(12,24,48,0.9))" }}
            />
          )}
          {/* Gentle scrim only on banner so the type icon stays legible —
              video covers come with their own internal scrim from BatchCover. */}
          {!hasVideo && (
            <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-black/40 sm:from-black/30 to-transparent pointer-events-none" />
          )}
          {!hasVideo && (
            <Icon size={14} className="absolute bottom-1.5 left-1.5 text-white/85 pointer-events-none" />
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 py-3 px-3 md:py-3 md:px-4 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`mono-label text-[9px] px-2 py-0.5 rounded-full border ${
                status === "open"
                  ? "border-cyan-glow/40 text-cyan-glow bg-cyan-glow/5"
                  : status === "upcoming"
                  ? "border-[#ffcb47]/40 text-[#ffcb47] bg-[#ffcb47]/5"
                  : "border-rose-300/40 text-rose-300 bg-rose-300/5"
              }`}>
                {status === "open" ? "OPEN" : status === "upcoming" ? "SOON" : "CLOSED"}
              </span>
              {batch.price_one_time !== null && batch.price_one_time !== undefined && (
                <span className="font-body text-[10px] sm:text-xs text-white/55 whitespace-nowrap">Rs. {Number(batch.price_one_time).toLocaleString()}</span>
              )}
            </div>
            <div className="font-body text-white text-[13px] sm:text-sm md:text-base font-medium line-clamp-2 sm:truncate">{batch.name}</div>
            <div className="mono-label text-white/45 text-[9px] sm:text-[10px] truncate mt-0.5 sm:block">
              {batch.audience || batch.tagline || "MDCAT cohort"}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center justify-center transition"
                title="WhatsApp"
                aria-label={`WhatsApp ${batch.name}`}
                data-testid={`access-batch-wa-${batch.slug}`}
              >
                <WhatsAppIcon size={14} />
              </a>
            )}
            {canApply ? (
              <Link
                to={`/register/${batch.slug}`}
                className="inline-flex items-center gap-1 h-9 px-3 md:px-4 rounded-full bg-cyan-glow text-ink-900 font-semibold text-[11px] sm:text-xs whitespace-nowrap hover:bg-cyan-glow/90 transition"
                data-testid={`access-batch-apply-${batch.slug}`}
              >
                Apply <ArrowRight size={12} />
              </Link>
            ) : (
              <Link
                to={`/apply`}
                className="inline-flex items-center gap-1 h-9 px-3 md:px-4 rounded-full border border-white/15 text-white/70 font-body text-[11px] sm:text-xs whitespace-nowrap hover:border-white/30 hover:text-white transition"
                data-testid={`access-batch-details-${batch.slug}`}
              >
                Details
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Start/end dates strip */}
      {(batch.start_date || batch.end_date) && (
        <div className="relative px-3 md:px-4 pb-2 flex items-center gap-2 text-[9px] sm:text-[10px] text-white/45">
          <Calendar size={10} />
          {batch.start_date && <span>Starts {new Date(batch.start_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>}
          {batch.end_date && <span>· Ends {new Date(batch.end_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>}
        </div>
      )}
    </motion.div>
  );
}
